import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  fetchTrainerEbayErrorLogs,
  resolveTrainerEbayErrorLog,
} from "./trainerInventoryApi";
import { isHistoricalListingPrerequisite, partitionLegacyEbayErrors } from "./trainerLegacyEbayReviewUtils";

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
};

const contextLabel = (errorLog) => {
  const parts = [
    errorLog.trainer_inventory_intake_row_id ? `Intake #${errorLog.trainer_inventory_intake_row_id}` : null,
    errorLog.trainer_inventory_summary_id ? `Inventory #${errorLog.trainer_inventory_summary_id}` : null,
    errorLog.listing_id ? `Listing #${errorLog.listing_id}` : null,
    errorLog.sku ? `SKU ${errorLog.sku}` : null,
    errorLog.ebay_listing_id ? `eBay ${errorLog.ebay_listing_id}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" | ") : "-";
};

export default function TrainerEbayErrorLogPanel({ refreshToken = 0 }) {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [notesById, setNotesById] = useState({});
  const [resolvingById, setResolvingById] = useState({});

  const loadErrors = async () => {
    setLoading(true);
    setMessage("");
    try {
      const response = await fetchTrainerEbayErrorLogs({
        unresolved_only: 1,
        limit: 20,
      });
      setErrors(response.data?.errors || []);
    } catch (error) {
      setMessage(error.response?.data?.error || "Failed to load eBay error logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const markResolved = async (errorLogId) => {
    setResolvingById((prev) => ({ ...prev, [errorLogId]: true }));
    setMessage("");
    try {
      await resolveTrainerEbayErrorLog(errorLogId, notesById[errorLogId] || "");
      setNotesById((prev) => ({ ...prev, [errorLogId]: "" }));
      await loadErrors();
    } catch (error) {
      setMessage(error.response?.data?.error || "Failed to resolve eBay error");
    } finally {
      setResolvingById((prev) => ({ ...prev, [errorLogId]: false }));
    }
  };

  // Old unresolved prerequisite rows must stay auditable. Distinguish them
  // from genuine API failures rather than deleting or auto-resolving them.
  const { errors: apiErrors, priorPrerequisites } = partitionLegacyEbayErrors(errors);

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, mb: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1">Recent eBay Errors</Typography>
          <Typography variant="body2" color="text.secondary">
            Unresolved eBay API failures. Earlier missing-listing records are preserved below as listing prerequisites, not API failures.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" color={apiErrors.length ? "error" : "success"} label={`${apiErrors.length} API errors`} />
          {priorPrerequisites.length > 0 && (
            <Chip size="small" color="warning" label={`${priorPrerequisites.length} prior listing prerequisites`} />
          )}
          <Button variant="outlined" onClick={loadErrors} disabled={loading}>
            Refresh Errors
          </Button>
        </Stack>
      </Stack>

      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
      {loading && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading eBay errors</Typography>
        </Stack>
      )}

      <TableContainer sx={{ maxHeight: 280, border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Operation</TableCell>
              <TableCell>Context</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Resolve</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.map((errorLog) => (
              <TableRow key={errorLog.id} hover>
                <TableCell sx={{ minWidth: 150 }}>{formatDateTime(errorLog.created_at)}</TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Chip
                      size="small"
                      color={isHistoricalListingPrerequisite(errorLog) ? "warning" : "default"}
                      label={isHistoricalListingPrerequisite(errorLog) ? "Prior listing prerequisite" : (errorLog.operation || "-")}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {errorLog.error_type || "-"}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2">{contextLabel(errorLog)}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <Typography variant="body2">
                    {isHistoricalListingPrerequisite(errorLog)
                      ? "No linked listing at the time. Link the exact existing listing or create an internal draft, then refresh. This historical entry is not an eBay API failure."
                      : String(errorLog.error_message || "-").slice(0, 320)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                    <TextField
                      size="small"
                      label="Resolution notes"
                      value={notesById[errorLog.id] || ""}
                      onChange={(event) =>
                        setNotesById((prev) => ({ ...prev, [errorLog.id]: event.target.value }))
                      }
                    />
                    <Button
                      variant="contained"
                      onClick={() => markResolved(errorLog.id)}
                      disabled={Boolean(resolvingById[errorLog.id])}
                    >
                      Resolve
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && errors.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>No unresolved Trainer Inventory eBay errors or historical listing prerequisites.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
